import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Crown, Check, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { openExternalUrl } from '@/utils/openExternalUrl';

interface UpgradeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reason?: string; // contextual message, e.g. "You've used your free tournament for this year."
}

const FEATURES = [
  'Unlimited matches (regular + tournaments)',
  'Full statistics & partnership history',
  'Hall of Fame & yearly rankings',
  'Priority support',
  'Premium badge on your profile',
];

export const UpgradeDialog: React.FC<UpgradeDialogProps> = ({ open, onOpenChange, reason }) => {
  const [interval, setInterval] = useState<'month' | 'year'>('year');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      const planCode = interval === 'month' ? 'premium_monthly' : 'premium_yearly';
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { plan_code: planCode },
      });
      if (error) throw error;
      if (data?.url) {
        await openExternalUrl(data.url);
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err: any) {
      console.error('Upgrade error:', err);
      toast({
        title: "Couldn't start checkout",
        description:
          (err?.message ?? 'Please try again in a moment.') +
          ' No payment was taken.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-full bg-primary/10 text-primary">
              <Crown className="h-5 w-5" />
            </div>
            <DialogTitle>Upgrade to Premium</DialogTitle>
          </div>
          <DialogDescription>
            {reason ?? 'Unlock everything LuvALL has to offer.'}
          </DialogDescription>
        </DialogHeader>

        {/* Plan toggle */}
        <div className="grid grid-cols-2 gap-2 p-1 rounded-lg bg-muted">
          <button
            type="button"
            onClick={() => setInterval('month')}
            className={cn(
              'rounded-md py-2 text-sm font-medium transition',
              interval === 'month' ? 'bg-background shadow-sm' : 'text-muted-foreground'
            )}
          >
            Monthly
          </button>
          <button
            type="button"
            onClick={() => setInterval('year')}
            className={cn(
              'rounded-md py-2 text-sm font-medium transition relative',
              interval === 'year' ? 'bg-background shadow-sm' : 'text-muted-foreground'
            )}
          >
            Yearly
            <Badge className="absolute -top-2 -right-2 bg-primary text-primary-foreground text-[10px] px-1.5 py-0">
              Save 33%
            </Badge>
          </button>
        </div>

        {/* Price card */}
        <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 text-center">
          <div className="text-3xl font-bold text-foreground">
            {interval === 'month' ? '€3' : '€24'}
            <span className="text-sm text-muted-foreground font-normal">
              {' '}/ {interval === 'month' ? 'month' : 'year'}
            </span>
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {interval === 'year' ? '€2/month, billed annually · ' : ''}Taxes included
          </div>
        </div>

        {/* Features list */}
        <ul className="space-y-2">
          {FEATURES.map((f) => (
            <li key={f} className="flex items-start gap-2 text-sm">
              <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />
              <span>{f}</span>
            </li>
          ))}
        </ul>

        <Button
          onClick={handleUpgrade}
          disabled={loading}
          className="w-full min-h-[44px]"
          size="lg"
        >
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Crown className="h-4 w-4 mr-2" />}
          Upgrade to Premium
        </Button>
        <p className="text-[11px] text-muted-foreground text-center">
          Cancel anytime from your billing settings.
        </p>
      </DialogContent>
    </Dialog>
  );
};

export default UpgradeDialog;
